import GuestLayout from "../../Layouts/GuestLayout";
import {
  TextField,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  FormControl,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import SecondaryButton from "../../Components/SecondaryButton";
import GroupedContainer from "../../Components/GroupedContainer";
import { useState } from "react";
import axios from "axios";

export default function UserRegister() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeMessages, setAgreeMessages] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const parts = [];

    if (digits.length > 0) parts.push(digits.slice(0, 3));
    if (digits.length > 3) parts.push(digits.slice(3, 6));
    if (digits.length > 6) parts.push(digits.slice(6, 10));

    return parts.join("-");
  };

  const handleFieldChange = (field) => (e) => {
    let value = e.target.value;

    if (field === "phone") {
      value = formatPhoneNumber(value);
    }

    setFormData({ ...formData, [field]: value });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateFields = () => {
    const errs = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;

    if (!formData.email) {
      errs.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errs.email = "Invalid email format";
    }

    if (!formData.firstName) errs.firstName = "First name is required";
    if (!formData.lastName) errs.lastName = "Last name is required";
    if (!formData.username) errs.username = "Username is required";

    if (!formData.phone) {
      errs.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone)) {
      errs.phone = "Phone must be in the format 999-999-9999";
    }

    if (!formData.password) {
      errs.password = "Password is required";
    } else {
      if (formData.password.length < 6) {
        errs.password = "Password must be at least 6 characters";
      } else {
        const hasLetter = /[A-Za-z]/.test(formData.password);
        const hasNumber = /\d/.test(formData.password);

        if (!hasLetter || !hasNumber) {
          errs.password = "Password must contain at least one letter and one number";
        }
      }
    }

    if (!formData.confirmPassword) {
      errs.confirmPassword = "Confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }

    // Validar checkboxes
    if (!agreeMessages) errs.agreeMessages = "You must agree to receive messages";
    if (!agreeTerms) errs.agreeTerms = "You must agree to the terms of service and privacy policy";

    return errs;
  };

  const clearForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    });
    setAgreeMessages(false);
    setAgreeTerms(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSnackbarMessage("");

    const fieldErrors = validateFields();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      setSnackbarMessage(Object.values(fieldErrors)[0]);
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      const partnerCompanyId = localStorage.getItem("companyId") || 1;
      const userRoleLocal = localStorage.getItem("userRoleId") || 3;

      const registerData = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        username: formData.username,
        password: formData.password,
        c_password: formData.confirmPassword,
        user_role_id: userRoleLocal,
        partner_company_id: partnerCompanyId,
      };

      await axios.post("https://new.cajuu.app/api/register", registerData);

      localStorage.setItem("registrationSuccess", "true");
      localStorage.removeItem("companyId");
      localStorage.removeItem("userRoleId");

      clearForm();
      window.location.href = "/login";
    } catch (err) {
      if (err.response?.data?.email) {
        setSnackbarMessage("The email address is already in use.");
      } else if (err.response?.data?.message) {
        setSnackbarMessage(`Registration failed: ${err.response.data.message}`);
      } else {
        setSnackbarMessage("An unknown error occurred.");
      }
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GuestLayout title="User Register">
      <div className="w-full mt-4 sm:mt-5 flex justify-center items-center">
        <div className="flex text-xl sm:text-2xl md:text-3xl tracking-tight text-amber-500 font-semibold">
          Log in to
          <div className="text-white flex items-center">
            <img src="/img/cajuu.png" alt="Cajuu Logo" className="w-7 h-7 sm:w-9 sm:h-9 ml-2" />
            Cajuu<span className="text-[#2f2f2f]/40">.</span>
          </div>
        </div>
      </div>

      <GroupedContainer className="w-full mt-6 sm:mt-8">
        <div className="grid grid-cols-1 w-full sm:grid-cols-2 gap-4 sm:gap-6">
          <TextField
            label="First Name"
            required
            color="secondary"
            value={formData.firstName}
            onChange={handleFieldChange("firstName")}
            error={!!errors.firstName}
            helperText={errors.firstName}
            className="w-full"
          />
          <TextField
            label="Last Name"
            required
            color="secondary"
            value={formData.lastName}
            onChange={handleFieldChange("lastName")}
            error={!!errors.lastName}
            helperText={errors.lastName}
            className="w-full"
          />
        </div>
      </GroupedContainer>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 mt-7">
        <TextField
          label="Username"
          required
          color="secondary"
          value={formData.username}
          onChange={handleFieldChange("username")}
          error={!!errors.username}
          helperText={errors.username}
          className="w-full"
        />
        <TextField
          label="Email"
          required
          color="secondary"
          value={formData.email}
          onChange={handleFieldChange("email")}
          error={!!errors.email}
          helperText={errors.email}
          className="w-full"
        />
        <TextField
          label="Phone"
          required
          color="secondary"
          value={formData.phone}
          onChange={handleFieldChange("phone")}
          error={!!errors.phone}
          helperText={errors.phone}
          className="w-full"
        />

        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          required
          color="secondary"
          value={formData.password}
          onChange={handleFieldChange("password")}
          error={!!errors.password}
          helperText={errors.password}
          className="w-full"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                  {showPassword ? <VisibilityOff className="text-white" /> : <Visibility className="text-white" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          required
          color="secondary"
          value={formData.confirmPassword}
          onChange={handleFieldChange("confirmPassword")}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          className="w-full"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                  {showConfirmPassword ? <VisibilityOff className="text-white" /> : <Visibility className="text-white" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Checkboxes */}
        <div className="space-y-4 mt-4">
          {/* Messages Checkbox */}
          <FormControl error={!!errors.agreeMessages} className="w-full">
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeMessages}
                  onChange={(e) => setAgreeMessages(e.target.checked)}
                  color="secondary"
                  sx={{
                    color: errors.agreeMessages ? '#ef4444' : '#ffffff',
                    '&.Mui-checked': {
                      color: '#f59e0b',
                    },
                  }}
                />
              }
              label={
                <span className="text-white text-sm">
                  I agree to receive text and e-mail messages from Cajuu including offers and promotions.
                </span>
              }
              className="w-full"
            />
            {errors.agreeMessages && (
              <p className="text-red-500 text-xs mt-1 ml-9">{errors.agreeMessages}</p>
            )}
          </FormControl>

          {/* Terms Checkbox */}
          <FormControl error={!!errors.agreeTerms} className="w-full">
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  color="secondary"
                  sx={{
                    color: errors.agreeTerms ? '#ef4444' : '#ffffff',
                    '&.Mui-checked': {
                      color: '#f59e0b',
                    },
                  }}
                />
              }
              label={
                <span className="text-white text-sm">
                  I agree to the{' '}
                  <a
                    href="https://cajuu.app/terms-of-use/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-500 hover:text-amber-400 underline transition-colors duration-200"
                  >
                    terms of service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://cajuu.app/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-500 hover:text-amber-400 underline transition-colors duration-200"
                  >
                    privacy policy
                  </a>
                </span>
              }
              className="w-full"
            />
            {errors.agreeTerms && (
              <p className="text-red-500 text-xs mt-1 ml-9">{errors.agreeTerms}</p>
            )}
          </FormControl>
        </div>
      </div>

      <div className="mt-6 sm:mt-10">
        <SecondaryButton
          onClick={handleSubmit}
          disabled={isLoading}
          className={`bg-amber-500 hover:bg-amber-600 hover:text-white border-none h-12 text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              Loading...
            </span>
          ) : (
            "Submit"
          )}
        </SecondaryButton>

        <div className="flex w-full justify-center sm:flex-row items-center mt-6 sm:mt-8">
          <a
            href="/login"
            className="underline underline-offset-4 hover:decoration-4 text-sm font-medium text-white hover:text-amber-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary mb-2 sm:mb-0"
          >
            Already have an account?
          </a>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{
            width: "500px",
            backgroundColor: "#dc2626",
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </GuestLayout>
  );
}